import { QueryConfig } from "@/lib/react-query";
import { ScenarioBuilderClient } from "@/pages/scenario-builder/api/client/scenarioClient";
import { useScenarioBuilderClient } from "@/pages/scenario-builder/api/hooks/useScenarioBuilderClient";
import { isDefined } from "@/Utils/array-utils";
import {
  GetScenarioTimeseriesResultsRequest,
  MarketingRevenueRangeTimeSeries,
} from "@mutiny-group/sbo-openapi-client";
import { useQuery } from "@tanstack/react-query";
import { isEmpty } from "lodash";

type GetScenarioTimeSeriesResultsOptions = GetScenarioTimeseriesResultsRequest & {
  scenarioBuilderClient: ScenarioBuilderClient;
};

const getScenarioTimeSeriesResults = async ({
  scenarioBuilderClient,
  ...request
}: GetScenarioTimeSeriesResultsOptions) => {
  return scenarioBuilderClient.scenarios.getScenarioTimeseriesResults(request);
};

type UseGetScenarioTimeSeriesResultsOptions = GetScenarioTimeseriesResultsRequest & {
  config?: QueryConfig<typeof getScenarioTimeSeriesResults>;
};

export type ExtendedMarketingRevenueRangeTimeSeries = MarketingRevenueRangeTimeSeries & {
  confidenceInterval?: number;
  changeFromHistoricSpend?: number;
};

export const useScenarioTimeSeriesResults = ({
  config,
  scenarioUuid,
  ...rest
}: UseGetScenarioTimeSeriesResultsOptions) => {
  const scenarioBuilderClient = useScenarioBuilderClient();
  return useQuery({
    ...config,
    queryKey: ["scenario-time-series-results", scenarioBuilderClient, scenarioUuid],
    queryFn: () =>
      getScenarioTimeSeriesResults({
        scenarioBuilderClient,
        scenarioUuid,
        ...rest,
      }),
    select: (data): ExtendedMarketingRevenueRangeTimeSeries[] => {
      if (isEmpty(data.marketingSpendRevenueRange)) {
        return [];
      }
      return data.marketingSpendRevenueRange.map(
        (spendRevenueRange: MarketingRevenueRangeTimeSeries) => {
          const { revenueUpperBound, revenueLowerBound, allocationShift } = spendRevenueRange;

          const confidenceInterval =
            isDefined(revenueUpperBound) && isDefined(revenueLowerBound)
              ? (revenueUpperBound - revenueLowerBound) / 2
              : undefined;
          const changeFromHistoricSpend = isDefined(allocationShift)
            ? allocationShift * 100
            : undefined;

          return {
            ...spendRevenueRange,
            confidenceInterval,
            changeFromHistoricSpend,
          };
        }
      );
    },
  });
};
